import { useQuery } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { api, unwrap } from "./client";
import type { Operation } from "./contracts";

const ACTIVE = ["executing", "undoing"];

export interface OperationFilter {
  status?: string;
  all?: boolean;
}

export function useOperations(filter: MaybeRefOrGetter<OperationFilter> = {}) {
  return useQuery({
    queryKey: ["operations", computed(() => toValue(filter))],
    queryFn: async () => {
      const f = toValue(filter);
      const res = await unwrap(
        api.GET("/api/v2/operations", { params: { query: { status: f.status || undefined, all: f.all ? "true" : undefined } } }),
      );
      return res.items as unknown as Operation[];
    },
    refetchInterval: (query) => (query.state.data?.some((o) => ACTIVE.includes(o.status)) ? 3_000 : 30_000),
  });
}

export function useOperation(id: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: ["operations", "one", computed(() => toValue(id))],
    queryFn: () => unwrap(api.GET("/api/v2/operations/{id}", { params: { path: { id: toValue(id) } } })) as unknown as Promise<Operation>,
    refetchInterval: (query) => (query.state.data && ACTIVE.includes(query.state.data.status) ? 2_000 : false),
  });
}

export const approveOperation = (id: string, typed?: string, breakGlassReason?: string) =>
  unwrap(api.POST("/api/v2/operations/{id}/approve", { params: { path: { id } }, body: { typed, breakGlassReason } }));

export const declineOperation = (id: string) => unwrap(api.POST("/api/v2/operations/{id}/decline", { params: { path: { id } } }));

/** Undo needs what the change needed: `typed` for high risk; under four-eyes it returns a pending undo (operationId) to approve. */
export const undoOperation = (id: string, typed?: string) =>
  unwrap(api.POST("/api/v2/operations/{id}/undo", { params: { path: { id } }, body: { typed } }));

/** What a high-risk approval (or undo) must be typed as (same rule as the server). */
export function confirmationText(op: Pick<Operation, "plan" | "target" | "tool">): string {
  return op.plan.typeToConfirm ?? op.target ?? op.tool;
}
